import { supabase } from '../lib/supabase';
import {
  UserProduct,
  NewUserProduct,
  UpdateUserProduct,
  UserProductWithDetails,
  ProductVariant,
  ProductModel,
  ProductFamily,
  Brand,
  RemovalReason,
  SYSTEM_OWNED_USER_PRODUCT_COLUMNS,
} from '../types/newDatabaseTypes';
import { attributesEqual } from './jsonCompare';

const itemReads = new Map<string, Promise<UserProductWithDetails | null>>();
const listReads = new Map<string, Promise<UserProductWithDetails[]>>();
export function getUserProducts(userId: string): Promise<UserProductWithDetails[]> {
  const existing = listReads.get(userId);
  if (existing) return existing;
  const request = readUserProducts(userId).finally(() => listReads.delete(userId));
  listReads.set(userId, request);
  return request;
}
export async function getUserProductById(id: string): Promise<UserProductWithDetails | null> {
  // Share requests only within the current authenticated subject. An account switch must
  // never reuse a previous owner's in-flight detail response.
  const { data, error } = await supabase.auth.getSession();
  if (error) return null;
  const owner = data.session?.user.id ?? null;
  const key = `${owner ?? 'signed-out'}:${id}`;
  const existing = itemReads.get(key);
  if (existing) return existing;
  const request = (async () => {
    const product = await readUserProductById(id);
    const current = await supabase.auth.getSession();
    if (current.error || (current.data.session?.user.id ?? null) !== owner) return null;
    return product && owner !== null && product.user_id !== owner ? null : product;
  })().finally(() => itemReads.delete(key));
  itemReads.set(key, request);
  return request;
}

// ---------------------------------------------------------------------------
// Query & shaping helpers
// ---------------------------------------------------------------------------

// Convert the raw response from Supabase into the strongly-typed
// UserProductWithDetails shape used throughout the app. Items with
// variant_id=NULL (backend still matching) are still returned — the
// variant/model/family/brand fields will be null and the UI falls back
// to image_analysis data for display.
function shapeUserProduct(data: any): UserProductWithDetails | null {
  if (!data) return null;

  const { product_variants, ...userProductData } = data;

  // When variant_id is NULL (backend hasn't finished catalog matching),
  // product_variants comes back null from the left join. Return with
  // variant=null so the item still appears in the cache list. The
  // UserProductWithDetails type allows null at every relation level.
  if (!product_variants) {
    return { ...userProductData, variant: null };
  }

  const { product_models, ...variantData } = product_variants;
  if (!product_models) {
    return { ...userProductData, variant: { ...variantData, model: null } };
  }

  const { product_families, ...modelData } = product_models;
  if (!product_families) {
    return {
      ...userProductData,
      variant: { ...variantData, model: { ...modelData, family: null } },
    };
  }

  const { brands, ...familyData } = product_families;

  return {
    ...userProductData,
    variant: {
      ...variantData,
      model: {
        ...modelData,
        family: {
          ...familyData,
          brand: brands ?? null,
        },
      },
    },
  };
}

/**
 * Fetches all user products with their related data.
 * Uses the authenticated S1b RPC; errors fail closed without a raw-table fallback.
 */
async function readUserProducts(userId: string): Promise<UserProductWithDetails[]> {
  try {
    const { data, error } = await supabase.rpc('restoration_read_items');
    if (error) throw error;
    return ((data ?? []) as any[]).map(shapeUserProduct)
      .filter((p): p is UserProductWithDetails => p !== null && p.user_id === userId);
  } catch (error) {
    console.error('Error in getUserProducts:', (error as Error).message);
    throw error;
  }
}

/**
 * Fetches a single user product by ID
 */
async function readUserProductById(id: string): Promise<UserProductWithDetails | null> {
  try {
    const { data, error } = await supabase.rpc('restoration_read_items', { p_item_ids: [id] });
    if (error) throw error;
    return shapeUserProduct(((data ?? []) as any[]).find(row => row.id === id));

  } catch (error) {
    console.error('Error in getUserProductById:', (error as Error).message);
    return null;
  }
}

/**
 * Adds a new user product
 * Note: This requires the variant to already exist
 */
/** V3 P7 (C9): drop any system-owned column an untyped caller tries to write. */
function withoutSystemOwnedColumns<T extends object>(row: T): T {
  const copy = { ...(row as unknown as Record<string, unknown>) };
  for (const column of SYSTEM_OWNED_USER_PRODUCT_COLUMNS) delete copy[column];
  return copy as unknown as T;
}

export async function addUserProduct(product: NewUserProduct): Promise<UserProduct | null> {
  try {
    const { data, error } = await supabase
      .from('user_products')
      .insert(withoutSystemOwnedColumns(product))
      .select()
      .single();

    if (error) {
      console.error('Error adding user product:', error);
      return null;
    }

    return data as unknown as UserProduct;
  } catch (error) {
    console.error('Error in addUserProduct:', error);
    return null;
  }
}

/**
 * Updates an existing user product
 */
export async function updateUserProduct(id: string, updates: UpdateUserProduct): Promise<UserProductWithDetails | null> {
  try {
    // First, perform the update
    const { error: updateError } = await supabase
      .from('user_products')
      .update(withoutSystemOwnedColumns(updates))
      .eq('id', id);

    if (updateError) {
      console.error('Error updating user product:', updateError?.message ?? updateError);
      return null;
    }

    console.log('✅ Update successful, waiting for DB consistency...');
    
    // Small delay to ensure database consistency
    await new Promise(resolve => setTimeout(resolve, 100));

    // Then fetch the updated product with full nested relationships
    // This ensures we get all the nested data properly
    const product = await getUserProductById(id);
    
    if (product) {
      console.log('✅ Fetched updated product with variant:', product.variant_id);
    } else {
      console.error('❌ Failed to fetch updated product');
    }
    
    return product;

  } catch (error) {
    console.error('Error in updateUserProduct:', (error as Error).message);
    return null;
  }
}

/**
 * Deletes a user product
 */
export async function deleteUserProduct(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('user_products')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting user product:', error);
      throw error;
    }

    console.log('Deleted user product:', id);
  } catch (error) {
    console.error('Error in deleteUserProduct:', error);
    throw error;
  }
}

/**
 * Finds or creates a product variant based on brand, family, model and attributes
 * This is a helper function for adding new items when the variant doesn't exist yet
 */
export async function findOrCreateProductVariant(
  brandName: string,
  familyName: string,
  modelName: string,
  attributes: Record<string, any>
): Promise<ProductVariant | null> {
  try {
    // 1. Find or create brand
    let brand: Brand | null = null;
    const { data: existingBrands } = await supabase
      .from('brands')
      .select('id, name')
      .eq('name', brandName)
      .limit(1);
    
    if (existingBrands && existingBrands.length > 0) {
      brand = existingBrands[0];
    } else {
      const { data: newBrand, error: brandError } = await supabase
        .from('brands')
        .insert({ name: brandName })
        .select('id, name')
        .single();
      
      if (brandError) throw brandError;
      brand = newBrand;
    }
    
    if (!brand) throw new Error('Failed to find or create brand');
    
    // 2. Find or create product family
    let family: ProductFamily | null = null;
    const { data: existingFamilies } = await supabase
      .from('product_families')
      .select('id, name, brand_id')
      .eq('name', familyName)
      .eq('brand_id', brand.id)
      .limit(1);
    
    if (existingFamilies && existingFamilies.length > 0) {
      family = existingFamilies[0];
    } else {
      const { data: newFamily, error: familyError } = await supabase
        .from('product_families')
        .insert({ name: familyName, brand_id: brand.id })
        .select('id, name, brand_id')
        .single();
      
      if (familyError) throw familyError;
      family = newFamily;
    }
    
    if (!family) throw new Error('Failed to find or create product family');
    
    // 3. Find or create product model
    //    Filter the lookup by brand as well so we don't bypass an existing
    //    branded twin and create a brand-less duplicate. The insert now sets
    //    brand_uuid_id so frontend-created models match the shape of the
    //    seeded/backend-created ones.
    let model: ProductModel | null = null;
    const { data: existingModels } = await supabase
      .from('product_models')
      .select('id, name, product_family_id')
      .eq('name', modelName)
      .eq('product_family_id', family.id)
      .eq('brand_uuid_id', brand.id)
      .limit(1);

    if (existingModels && existingModels.length > 0) {
      model = existingModels[0];
    } else {
      const { data: newModel, error: modelError } = await supabase
        .from('product_models')
        .insert({ name: modelName, product_family_id: family.id, brand_uuid_id: brand.id })
        .select('id, name, product_family_id')
        .single();

      if (modelError) throw modelError;
      model = newModel;
    }
    
    if (!model) throw new Error('Failed to find or create product model');
    
    // 4. Find or create product variant
    let variant: ProductVariant | null = null;
    const { data: existingVariants } = await supabase
      .from('product_variants')
      .select('id, model_id, attributes')
      .eq('model_id', model.id)
      .limit(10);

    // Use key-order-insensitive comparison (JSONB returns keys alphabetically)
    if (existingVariants) {
      variant = existingVariants.find(v =>
        attributesEqual(v.attributes as Record<string, unknown>, attributes as Record<string, unknown>)
      ) || null;
    }
    
    if (!variant) {
      const { data: newVariant, error: variantError } = await supabase
        .from('product_variants')
        .insert({ model_id: model.id, attributes })
        .select('id, model_id, attributes')
        .single();
      
      if (variantError) throw variantError;
      variant = newVariant;
    }
    
    return variant;
  } catch (error) {
    console.error('Error finding or creating product variant:', error);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Status transition functions
// ---------------------------------------------------------------------------

/**
 * Removes a user product (soft delete) with removal metadata
 */
export async function removeUserProduct(
  productId: string,
  reason: RemovalReason,
  notes?: string
): Promise<UserProductWithDetails | null> {
  try {
    // Get current product to capture valuation
    const currentProduct = await getUserProductById(productId);
    if (!currentProduct) {
      throw new Error('Product not found');
    }

    const updates: UpdateUserProduct = {
      status: 'removed',
      removed_at: new Date().toISOString(),
      removal_price: currentProduct.valuation,
      removal_reason: reason,
      removal_notes: notes || null,
    };

    return await updateUserProduct(productId, updates);
  } catch (error) {
    console.error('Error removing user product:', error);
    return null;
  }
}

/**
 * Restores a removed product back to owned status
 */
export async function restoreUserProduct(
  productId: string
): Promise<UserProductWithDetails | null> {
  try {
    const updates: UpdateUserProduct = {
      status: 'owned',
      removed_at: null,
      removal_price: null,
      removal_reason: null,
      removal_notes: null,
    };

    return await updateUserProduct(productId, updates);
  } catch (error) {
    console.error('Error restoring user product:', error);
    return null;
  }
}

/**
 * Converts a tracking item to owned with purchase details
 */
export async function markTrackingAsOwned(
  productId: string,
  purchasePrice?: number,
  purchasePeriod?: string,
  condition: string = 'Used'
): Promise<UserProductWithDetails | null> {
  try {
    const updates: UpdateUserProduct = {
      status: 'owned',
      purchase_price: purchasePrice || null,
      purchase_period: purchasePeriod || null,
      condition,
    };

    return await updateUserProduct(productId, updates);
  } catch (error) {
    console.error('Error marking tracking as owned:', error);
    return null;
  }
}
